/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Form, Button, Badge, Card, Alert,
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import OperatorService from '../../../Services/OperatorService';
import {
  assignableOperators, expenseAccounts, isExpenseAccount,
  DRIVER_ROLE, EXPENSE_ROLE, steeredExpenseRole,
} from '../../../Utils/operatorType';
import { OPERATOR_ROLE_LABELS, operatorRoleBadge } from '../../../config/bookingConstants';

/** Every stored role, Cost last — the one table in bookingConstants. */
const OPERATOR_ROLES = Object.entries(OPERATOR_ROLE_LABELS)
  .map(([value, info]) => ({ value, ...info }));

const COST = OPERATOR_ROLE_LABELS[EXPENSE_ROLE];

const CURRENCIES = ['EUR', 'HUF'];

function MultiOperatorAssignment({
  operators = [],
  onChange,
  disabled = false,
  showPricing = true,
  showCashFromClient = false,
  hideDriverRole = false,
  defaultCurrency = 'EUR',
}) {
  const [allOperators, setAllOperators] = useState([]);

  // Fetch all operators on mount
  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const response = await OperatorService.fetchOperators();
        setAllOperators(response?.data?.data || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching operators:', err);
      }
    };
    fetchOperators();
  }, []);

  /**
   * `defaultCurrency` is the BOOKING's currency where the caller passes one.
   *
   * A new row used to open in EUR unconditionally, which on a HUF booking is
   * a figure in the wrong currency that nobody had to type — and the API
   * believes `operators[].currency` over the booking's, so it settles at the
   * wrong value rather than being caught. EUR remains the fallback for a
   * booking that has no currency yet.
   */
  const addOperator = (role = 'driver') => {
    const currency = defaultCurrency || 'EUR';
    const newOperator = {
      role,
      operator_id: null,
      price: null,
      currency,
      comments: '',
      cash_from_client: null,
      cash_from_client_currency: currency,
    };
    onChange([...operators, newOperator]);
  };

  const removeOperator = (index) => {
    const newOperators = operators.filter((_, i) => i !== index);
    onChange(newOperators);
  };

  const updateOperator = (index, field, value) => {
    const newOperators = [...operators];
    newOperators[index] = { ...newOperators[index], [field]: value };
    onChange(newOperators);
  };

  /** The full operator record behind an id, UNFILTERED — so a slot that
   *  already holds a bucket keeps showing its name. */
  const getOperatorById = (id) => allOperators.find((o) => o.Id === id);

  /**
   * Choosing an operator. A COST ACCOUNT CHOSEN INTO A CREW ROW BECOMES A
   * COST ROW: its role is steered to the one the API's `roleForExpenseLine`
   * would have given it — whatever role that account already holds on this
   * booking, else `expense` (`steeredExpenseRole`).
   *
   * Two reasons, one old and one from 2026-11-03:
   *  - AN EXPENSE ACCOUNT MAY NOT SIT IN THE DRIVER SLOT. `getPrimaryOperator`
   *    reads the driver slot as the booking's primary operator, so a bucket
   *    there re-homes the whole trip to a parking account. The API refuses
   *    it too (`OperatorAssignmentGuard`); steering is what means the office
   *    never sees the refusal.
   *  - THE HABIT WAS "ADD ASSISTANT, PICK THE BUCKET". Cost records were
   *    filed that way for want of a cost role, and left alone this would
   *    keep writing new `assistant` buckets after the stored ones are
   *    converted. Steering every crew row, not just the driver's, ends it.
   *
   * The pick is not refused, and the role select stays open afterwards: a
   * cost account may still be set to Guide, Coordinator or Assistant by
   * hand (casual labour run as an excursion's guide), never to Driver.
   */
  const chooseOperator = (index, operatorId) => {
    const operator = operatorId ? getOperatorById(operatorId) : null;
    const next = [...operators];
    const row = { ...next[index], operator_id: operatorId };

    if (operator && isExpenseAccount(operator) && row.role !== EXPENSE_ROLE) {
      row.role = steeredExpenseRole(operators, operatorId, index);
    }

    next[index] = row;
    onChange(next);
  };

  const decorateOperatorOption = (option) => {
    if (!option) return null;
    return {
      id: option.Id,
      label: option.name,
    };
  };

  /**
   * WHO THIS PICKER OFFERS — both kinds, on every booking type.
   *
   * It used to offer suppliers only, on the reasoning that this picker asks
   * "who DOES the work". That was wrong in practice: a real trip has parking
   * and fuel against it, and with buckets filtered out there was no way to
   * attach a cost operator to one by hand at all.
   *
   * So both groups are listed, SUPPLIERS FIRST and visually primary — they
   * are the default, and what you get by just typing — with the cost buckets
   * in their own labelled group below. The label is the whole safety
   * mechanism for mis-picks: the two kinds sit in one flat list with similar
   * names, and choosing "AU-AA-670 Parkolás" when you meant the supplier who
   * drove the trip must not be an easy mistake to make silently.
   *
   * What makes this safe is NOT the booking type — an expense booking only
   * changes which fields the form asks for, not who may be assigned. It is
   * the ROLE GUARD below: an expense account may never hold `driver`, because
   * `getPrimaryOperator` returns the driver slot as the booking's primary and
   * a bucket there re-homes the trip to a parking account.
   *
   * The expense group deliberately carries NO `active === 1` filter, which
   * the supplier group keeps untouched. That matches `FleetManagementModal`,
   * the only other picker that offers buckets, and is the safe way round: a
   * bucket record carrying no `active` field at all would otherwise vanish
   * from the pickers that exist to show it.
   *
   * A COST ROW (`expense`) OFFERS THE COST ACCOUNTS ONLY. The cost role is
   * not for a supplier — a human may not file a crew operator as a cost —
   * so the row that holds it lists nothing else, and the role select bars
   * Cost once a supplier is chosen. That keeps the rule in one direction
   * each way without steering a supplier to a crew role nobody chose.
   */
  const costOptions = useMemo(() => expenseAccounts(allOperators)
    .map(decorateOperatorOption)
    .map((o) => ({ ...o, group: COST.label, isExpense: true })), [allOperators]);

  const operatorOptions = useMemo(() => ([
    ...assignableOperators(allOperators)
      .filter((o) => o.active === 1)
      .map(decorateOperatorOption)
      .map((o) => ({ ...o, group: 'Operator', isExpense: false })),
    ...costOptions,
  ]), [allOperators, costOptions]);

  return (
    <div className="multi-operator-assignment">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">
          <i className="fa fa-users me-2" />
          Assigned Operators
          {operators.length > 0 && (
            <Badge bg="secondary" className="ms-2">{operators.length}</Badge>
          )}
        </h6>
        <div className="d-flex gap-1">
          {OPERATOR_ROLES
            .filter((role) => !(hideDriverRole && role.value === 'driver'))
            .map((role) => {
              // Only allow one driver per booking
              const hasDriver = operators.some((op) => op.role === 'driver');
              const isDriverDisabled = role.value === 'driver' && hasDriver;
              return (
                <Button
                  key={role.value}
                  variant={`outline-${role.color}`}
                  size="sm"
                  onClick={() => addOperator(role.value)}
                  disabled={disabled || isDriverDisabled}
                  title={isDriverDisabled ? 'Only one driver allowed per booking' : `Add ${role.label}`}
                >
                  <i className={`fa ${role.icon} me-1`} />
                  {role.label}
                </Button>
              );
            })}
        </div>
      </div>

      {operators.length === 0 && (
        <Alert variant="info" className="mb-0">
          <i className="fa fa-info-circle me-2" />
          No operators assigned. Click a role button above to add an operator.
        </Alert>
      )}

      {operators.map((op, index) => {
        const selectedOperatorData = op.operator_id
          ? getOperatorById(op.operator_id)
          : null;
        const selectedIsExpenseAccount = isExpenseAccount(selectedOperatorData);
        // A supplier is chosen: the cost role is not its to hold.
        const selectedIsSupplier = Boolean(selectedOperatorData) && !selectedIsExpenseAccount;
        // A cost account reads as a Cost whatever role it is stored under
        // (`operatorRoleBadge`: an unconverted bucket is still `assistant`).
        const roleInfo = operatorRoleBadge(op.role, selectedOperatorData);
        const opKey = `${op.role}-${op.operator_id || index}`;
        // Check if another operator already has driver role (not this one)
        const hasOtherDriver = operators.some((o, i) => i !== index && o.role === 'driver');
        // Cash left behind after the booking's payment type moved away from
        // Cash / Mixed Payment. The fields used to simply disappear while the
        // value stayed on the record, so a booking could carry a cash portion
        // nobody could see or clear. Keep it visible and editable, flagged,
        // until someone zeroes it deliberately.
        const orphanCash = !showCashFromClient && Number(op.cash_from_client) > 0;
        const showCashFields = showCashFromClient || orphanCash;

        return (
          <Card key={opKey} className="mb-2">
            <Card.Header className={`bg-${roleInfo.color} bg-opacity-10 py-2`}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <Badge bg={roleInfo.color}>
                    <i className={`fa ${roleInfo.icon} me-1`} />
                    {roleInfo.label}
                  </Badge>
                  <Form.Select
                    size="sm"
                    value={op.role}
                    onChange={(e) => updateOperator(index, 'role', e.target.value)}
                    disabled={disabled}
                    style={{ width: 'auto' }}
                  >
                    {OPERATOR_ROLES
                      .filter((role) => !(hideDriverRole && role.value === DRIVER_ROLE))
                      .map((role) => {
                        // The driver slot is closed to a cost bucket however
                        // it is reached — picking the bucket into a driver
                        // row steers the role, and this stops it being set
                        // back by hand afterwards. The mirror image: the
                        // cost role is closed to a supplier. A row that
                        // already holds it (the receipt path files a
                        // supplier that is not on the trip as `expense`)
                        // still shows it, and keeps it unless changed.
                        const barred = (role.value === DRIVER_ROLE
                          && (hasOtherDriver || selectedIsExpenseAccount))
                          || (role.value === EXPENSE_ROLE && selectedIsSupplier);
                        let suffix = '';
                        if (role.value === DRIVER_ROLE && selectedIsExpenseAccount) {
                          suffix = ' (not for a cost)';
                        } else if (role.value === DRIVER_ROLE && hasOtherDriver) {
                          suffix = ' (limit 1)';
                        } else if (role.value === EXPENSE_ROLE && selectedIsSupplier) {
                          suffix = ' (cost accounts only)';
                        }
                        return (
                          <option key={role.value} value={role.value} disabled={barred}>
                            {role.label}
                            {suffix}
                          </option>
                        );
                      })}
                  </Form.Select>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => removeOperator(index)}
                  disabled={disabled}
                >
                  <i className="fa fa-trash" />
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-3">
              <div className="row g-2">
                {/* Operator Selection */}
                <div className="col-12">
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">Operator</Form.Label>
                    <Typeahead
                      id={`operator-${index}-select`}
                      // See `operatorOptions` above: suppliers first, cost
                      // buckets in their own labelled group below — and a
                      // Cost row offers the buckets alone.
                      options={op.role === EXPENSE_ROLE ? costOptions : operatorOptions}
                      selected={selectedOperatorData
                        ? [decorateOperatorOption(selectedOperatorData)]
                        : []}
                      onChange={(selected) => chooseOperator(index, selected[0]?.id || null)}
                      placeholder="Select operator..."
                      disabled={disabled}
                      clearButton
                      renderMenuItemChildren={(option) => (
                        <div className="d-flex justify-content-between align-items-center gap-2">
                          <span className={option.isExpense ? 'text-muted' : undefined}>
                            {option.label}
                          </span>
                          <Badge bg={option.isExpense ? 'dark' : 'primary'}>
                            {option.group}
                          </Badge>
                        </div>
                      )}
                    />
                    {selectedIsExpenseAccount && (
                      <Form.Text className="text-muted" data-testid={`operator-${index}-cost-note`}>
                        <i className={`fa ${COST.icon} me-1`} />
                        A cost outside the operator&apos;s agreed price, not a
                        crew member. It cannot take the driver role.
                      </Form.Text>
                    )}
                  </Form.Group>
                </div>

                {/* Pricing */}
                {showPricing && (
                  <>
                    <div className="col-md-4 col-6">
                      <Form.Group>
                        <Form.Label className="small text-muted mb-1">Price</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          value={op.price || ''}
                          onChange={(e) => updateOperator(index, 'price', e.target.value ? Number(e.target.value) : null)}
                          placeholder="0"
                          min={0}
                          disabled={disabled}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-2 col-6">
                      <Form.Group>
                        <Form.Label className="small text-muted mb-1">Currency</Form.Label>
                        <Form.Select
                          size="sm"
                          value={op.currency || defaultCurrency || 'EUR'}
                          onChange={(e) => updateOperator(index, 'currency', e.target.value)}
                          disabled={disabled}
                        >
                          {CURRENCIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </>
                )}

                {/* Cash from Client (for mixed payment). Also rendered when
                    the payment type no longer implies cash but a non-zero
                    amount is still on the record — flagged in red so it can
                    be corrected rather than silently hidden. */}
                {showCashFields && (
                  <>
                    <div className="col-md-4 col-6">
                      <Form.Group>
                        <Form.Label
                          className={`small mb-1 ${orphanCash ? 'text-danger fw-semibold' : 'text-muted'}`}
                        >
                          {orphanCash && (
                            <i
                              className="fa fa-exclamation-triangle me-1"
                              title="This booking is not marked as a cash payment, but a cash amount is still recorded. Clear it or change the payment type."
                              data-testid={`orphan-cash-warning-${index}`}
                            />
                          )}
                          Cash from Client
                        </Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          className={orphanCash ? 'border-danger' : undefined}
                          value={op.cash_from_client || ''}
                          onChange={(e) => updateOperator(index, 'cash_from_client', e.target.value ? Number(e.target.value) : null)}
                          placeholder="0"
                          min={0}
                          disabled={disabled}
                        />
                        {orphanCash && (
                          <Form.Text className="text-danger">
                            Payment type is not cash — clear this or switch the
                            booking back to Cash / Mixed Payment.
                          </Form.Text>
                        )}
                      </Form.Group>
                    </div>
                    <div className="col-md-2 col-6">
                      <Form.Group>
                        <Form.Label className="small text-muted mb-1">Currency</Form.Label>
                        <Form.Select
                          size="sm"
                          className={orphanCash ? 'border-danger' : undefined}
                          value={op.cash_from_client_currency || defaultCurrency || 'EUR'}
                          onChange={(e) => updateOperator(index, 'cash_from_client_currency', e.target.value)}
                          disabled={disabled}
                        >
                          {CURRENCIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </>
                )}

                {/* Comments */}
                <div className="col-12">
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">Comments for this operator</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      size="sm"
                      value={op.comments || ''}
                      onChange={(e) => updateOperator(index, 'comments', e.target.value)}
                      placeholder="Special instructions..."
                      disabled={disabled}
                    />
                  </Form.Group>
                </div>
              </div>
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
}

export default MultiOperatorAssignment;
